import { colors as themeColors } from '../utils/theme';
import { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, TextField, MenuItem, IconButton, CircularProgress, Divider, Button, Checkbox } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatchCart, getVariantKey } from '../context/DispatchContext';
import Badge from '@mui/material/Badge';
import { MdShoppingCartCheckout } from 'react-icons/md';

const API_URL = `http://192.168.1.248:3103/api`;

export default function DispatchPage() {
    const { clientId } = useParams();
    const { cart, addItem } = useDispatchCart();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedSOIDs = location.state?.selectedSOIDs || [];
    const [loading, setLoading] = useState(true);
    const [colorOptions, setColorOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [clientData, setClientData] = useState(null);
    const [ordersData, setOrdersData] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]); // <== new state
    const [bounce, setBounce] = useState(false);

    useEffect(() => {
        if (Object.keys(cart).length > 0) {
            setBounce(true);
            const timer = setTimeout(() => setBounce(false), 500);
            return () => clearTimeout(timer);
        }
    }, [cart]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [orderRes, colorRes, sizeRes] = await Promise.all([
                    axios.post(`${API_URL}/orders-admin/${clientId}`, { selectedSOIDs }),
                    axios.get(`${API_URL}/colormaster`),
                    axios.get(`${API_URL}/sizemaster`)
                ]);
                console.log(orderRes.data);
                const { client, orders } = orderRes.data;
                setClientData({
                    ...client,
                    clientId: client.id || client.ClientID || '',  // 🔒 fallback if needed
                });

                const processedOrders = orders.map(order => ({
                    ...order,
                    items: order.items.map(item => {
                        const qty = +item.qty || 1;
                        const maxLevel = +item.maxLevel || 0;
                        const limit = Math.min(qty, maxLevel);
                        return {
                            ...item,
                            
                            SOAutoID: order.SOAutoID, // ✅ ensure this is present
                            selectedQty: qty,
                            selectedColor: +item.itemColor || '',
                            selectedSize: +item.itemSize || '',
                            originalColor: +item.itemColor || '',
                            originalSize: +item.itemSize || '',
                            originalQty: qty,
                            dispatchQty: limit,
                            maxLevel: maxLevel,
                        };
                    })
                }));

                setOrdersData(processedOrders);
                setColorOptions(colorRes.data || []);
                setSizeOptions(sizeRes.data || []);
            } catch (err) {
                console.error('Error fetching order data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [clientId, selectedSOIDs]);
    const handleCheckboxToggle = (SODtsID) => { setSelectedItems((prev) => prev.includes(SODtsID) ? prev.filter(id => id !== SODtsID) : [...prev, SODtsID]); };

    const handleDispatchAll = () => {
        console.log("Dispatching SODtsIDs:", selectedItems);

        const allItems = ordersData.flatMap(order => order.items);
        const selectedDispatchItems = allItems.filter(item => selectedItems.includes(item.SODtsID));

        selectedDispatchItems.forEach(item => {
            const productToAdd = {
                ...item,
                clientname: clientData.clientname,
                clientId: clientData.clientId,
                SOAutoID: item.SOAutoID,
                SODtsID: item.SODtsID,
                productId: item.ItemID,
                dispatchQty: item.dispatchQty,
                variantKey: getVariantKey({
                    clientname: clientData.clientname,
                    clientId: clientData.clientId,
                    SOAutoID: item.SOAutoID,
                    SODtsID: item.SODtsID
                })
            };

            addItem(productToAdd);
        });

        console.log("✅ Added to Dispatch Cart:", selectedDispatchItems.map(i => ({
            SODtsID: i.SODtsID,
            productId: i.ItemID
        })));
    };

    return (
        <Box p={0}>
            {clientData ? (
                <>
                    {/* ——— Sticky Header Start ——— */}
                    <Box position="sticky" top={50} zIndex={10} bgcolor="background.paper" sx={{ borderBottom: `1px solid ${themeColors.neutral.line}`, mb: 2, pt: 2, pb: 2 }} p={2} >
                        {/* Back + Client Name */}
                        <Box display="flex" alignItems="center" mb={1}>
                            <IconButton onClick={() => navigate(-1)}>
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h6" fontWeight={700} color="primary">
                                {clientData.clientname}
                            </Typography>
                        </Box>

                        {/* Client Info + Controls */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                            {/* Client Info */}
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    📍 {clientData.address}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    🏙️ {clientData.city}, {clientData.state}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    📞 {clientData.mobileNo} &nbsp;|&nbsp; ✉️ {clientData.emailId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    GSTIN: <strong>{clientData.gstno || '-'}</strong>
                                </Typography>
                            </Box>

                            {/* Select All + Dispatch Button */}
                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                                {clientData && Object.values(cart).some(item => item.clientId === clientData.clientId) && (
                                    <Badge badgeContent={Object.values(cart).filter(item => item.clientId === clientData.clientId).length} color="error" overlap="circular" sx={{ ml: 0 }} >
                                        <IconButton color="primary" size="large" onClick={() => navigate('/main/dispatch-cart', { state: { clientId: clientData.clientId } }) } sx={{animation: bounce ? 'bounce 0.5s ease' : 'none', '@keyframes bounce': { '0%,100%': { transform: 'translateY(0)' }, '30%': { transform: 'translateY(-8px)' }, } }} >
                                            <MdShoppingCartCheckout size={28} />
                                        </IconButton>
                                    </Badge>
                                )}
                                <Box display="flex" alignItems="center">
                                    <Checkbox checked={selectedItems.length > 0 && selectedItems.length === ordersData.flatMap(o => o.items).length}
                                        onChange={e => {
                                            const allIds = ordersData.flatMap(o => o.items.map(i => i.SODtsID));
                                            setSelectedItems(e.target.checked ? allIds : []);
                                        }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        Select All?
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" gap={1}>
                                    <Button variant="contained" color="primary" onClick={handleDispatchAll} disabled={selectedItems.length === 0} sx={{ height: 40 }} >
                                        Add to Dispatch Cart
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    {/* ——— Sticky Header End ——— */}
                </>
            ) : (
                <Box textAlign="center" py={4}>
                    <CircularProgress />
                </Box>
            )}

            {loading ? (
                <Box textAlign="center" py={5}><CircularProgress /></Box>
            ) : (
                ordersData.map((order, orderIdx) => (
                    <Box key={orderIdx} mx={3}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            📦 Order No: {order.SONumber} | Date: {new Date(order.SODate).toLocaleDateString('en-GB')}
                        </Typography>

                        {order.items.map((product, idx) => {
                            const rate = +product.saleRate || 0;
                            const gst = +product.taxName || product.igst;
                            const qty = +product.selectedQty || 1;
                            const maxLevel = +product.maxLevel || 0;
                            const exclTotal = rate * qty;
                            const gstAmt = (exclTotal * gst) / 100;
                            const inclTotal = exclTotal + gstAmt;
                            const borderColor = qty <= maxLevel ? '#25D366' : '#FF2800';
                            const hasChanged = product.selectedQty !== product.originalQty;

                            return (
                                <Paper key={idx} elevation={2}
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: themeColors.neutral.surfaceMuted,
                                        borderLeft: `4px solid ${borderColor}`
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                        <Box flexGrow={1} minWidth="280px">
                                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                <Checkbox checked={selectedItems.includes(product.SODtsID)} onChange={() => handleCheckboxToggle(product.SODtsID)} size="small" />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {product.itemCode}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" mb={0.5}>
                                                {product.description}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                                Unit: {product.unit} | HSN: {product.HSN}
                                            </Typography>

                                            <Box display="flex" gap={1} flexWrap="wrap" mb={1} alignItems="center">
                                                <TextField label="Size" select size="small" value={product.selectedSize} disabled sx={{ minWidth: 120 }} >
                                                    {sizeOptions.map(o => (
                                                        <MenuItem key={o.sizeId} value={o.sizeId}>
                                                            {o.itemSize}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>

                                                <TextField label="Color" select size="small" value={product.selectedColor} disabled sx={{ minWidth: 140 }} >
                                                    {colorOptions.map(o => (
                                                        <MenuItem key={o.colorId} value={o.colorId}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <Box
                                                                    sx={{
                                                                        width: 12,
                                                                        height: 12,
                                                                        borderRadius: '50%',
                                                                        bgcolor: o.colorCode,
                                                                        border: `1px solid ${themeColors.neutral.lineStrong}`,
                                                                    }}
                                                                />
                                                                {o.itemColor}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField label="Qty" size="small" value={qty} disabled sx={{ width: 60 }} />
                                                <TextField label="Stock" size="small" value={product.maxLevel || ''} disabled sx={{ width: 80 }} />
                                                <TextField label="Dispatch" type="number" size="small" value={product.dispatchQty} disabled={!selectedItems.includes(product.SODtsID)}
                                                    inputProps={{ min: 0, max: Math.min(qty, maxLevel), }} sx={{ width: 80 }}
                                                    onChange={e => {
                                                        const val = +e.target.value;
                                                        const limit = Math.min(qty, maxLevel);
                                                        if (val >= 0 && Number.isInteger(val) && val <= limit) {
                                                            const updated = [...ordersData];
                                                            updated[orderIdx].items[idx].dispatchQty = val;
                                                            setOrdersData(updated);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        const limit = Math.min(qty, maxLevel);
                                                        if (!Number.isInteger(product.dispatchQty) || product.dispatchQty < 0 || product.dispatchQty > limit) {
                                                            const updated = [...ordersData];
                                                            updated[orderIdx].items[idx].dispatchQty = limit;
                                                            setOrdersData(updated);
                                                        }
                                                    }}
                                                />
                                            </Box>

                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        ₹{rate}×{qty}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        = ₹{exclTotal}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        GST ({gst}%)
                                                    </Typography>
                                                    <Typography variant="body2">₹{gstAmt.toFixed(2)}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Box minWidth="160px" textAlign="right">
                                            <Typography variant="caption" color="text.secondary">
                                                Total
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight={700} color="primary">
                                                ₹{inclTotal.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {hasChanged && (
                                        <Box mt={1} textAlign="center">
                                            <Button variant="contained" color="primary" size="small" sx={{ borderRadius: 1, textTransform: 'none', px: 2 }} onClick={() => console.log(`Update OrderItem → OrderID=${order.SOAutoID} | ItemID=${product.ItemID}`)} >
                                                Update
                                            </Button>
                                        </Box>
                                    )}
                                </Paper>
                            );
                        })}
                        <Divider sx={{ mb: 1 }} />
                    </Box>
                ))
            )}
        </Box>
    );
}